import { Box, Typography } from "@mui/material"
import type { Row } from "@tanstack/react-table"
import { type ChangeEvent, type FC, useEffect, useState } from "react"

import { TabletWarehouseFilter } from "../Incoming/components"

import SearchInput from "@/components/SearchInput/SearchInput"
import { type GoodsReceipt, GoodsReceiptStatus } from "@/graphql/codegen/graphql"
import { NotificationType } from "@/schemas/notification"
import { CheckedInPurchaseOrdersTable, GoodsReceiptsTable } from "@/screens/WarehouseReceiving/components"
import { useGoodsReceiptsTable } from "@/screens/WarehouseReceiving/hooks"
import { useGoodsReceiptPalletActions } from "@/screens/WarehouseReceiving/hooks/useGoodsReceiptPalletActions"
import { useGetGoodsReceiptPalletsForPrint } from "@/screens/WarehouseReceiving/subfeatures/CheckedIn/components/FinalizeCheckInSuccessDialog/hooks/useGetGoodsReceiptPalletsForPrint"
import { useNotificationsStore } from "@/stores/useNotificationsStore"

const statuses = [GoodsReceiptStatus.CheckedIn]

export const TabletCheckedInGoodsReceiptsContainer: FC = () => {
  const {
    goodsReceipts,
    isGoodsReceiptsFetching,
    pageInfo,
    searchQuery,
    warehouseAreaIds,
    sortingState,
    tableContainerRef,
    handleSearchChange,
    handleFilterChange,
    handleSortingChange,
    handleScroll,
  } = useGoodsReceiptsTable({
    statuses,
    routePath: "/app/tablet/warehouse-receiving",
  })

  const [printGoodsReceiptId, setPrintGoodsReceiptId] = useState<string | null>(null)

  const { handleQrPrintAll } = useGoodsReceiptPalletActions()
  const { pallets, isFetching } = useGetGoodsReceiptPalletsForPrint(printGoodsReceiptId)

  const notify = useNotificationsStore((state) => state.enqueueNotification)

  useEffect(() => {
    if (!printGoodsReceiptId || isFetching) {
      return
    }

    if (pallets.length === 0) {
      notify({
        type: NotificationType.Info,
        silent: false,
        message: "No pallets found for printing.",
      })

      setPrintGoodsReceiptId(null)

      return
    }

    handleQrPrintAll(pallets)

    setPrintGoodsReceiptId(null)
  }, [pallets, isFetching, printGoodsReceiptId, handleQrPrintAll, notify])

  const handlePalletsPrint = (goodsReceipt: GoodsReceipt) => {
    setPrintGoodsReceiptId(goodsReceipt.goodsReceiptId)
  }

  const renderExpandedContent = (row: Row<GoodsReceipt>) => (
    <Box className='p-4'>
      <CheckedInPurchaseOrdersTable goodsReceipt={row.original} />
    </Box>
  )

  return (
    <Box className='flex h-full flex-col gap-y-6 p-6'>
      <SearchInput
        placeholder='Search by GR # or Reference #'
        defaultValue={searchQuery}
        onChange={(e: ChangeEvent<HTMLInputElement>) => handleSearchChange(e.target.value)}
        className='w-full'
      />

      <Box className='flex items-center justify-between'>
        <Typography variant='h6' className='font-bold text-gray-700'>
          {pageInfo?.total ?? 0} Checked In
        </Typography>
        <TabletWarehouseFilter
          selectedValue={warehouseAreaIds?.[0] || null}
          onChange={(warehouseId) => handleFilterChange(warehouseId ? [warehouseId] : [])}
        />
      </Box>

      <Box className='flex min-h-0 flex-1 overflow-hidden'>
        <GoodsReceiptsTable
          data={goodsReceipts}
          isLoading={isGoodsReceiptsFetching}
          sortingState={sortingState}
          tableContainerRef={tableContainerRef}
          status={GoodsReceiptStatus.CheckedIn}
          onSortingChange={handleSortingChange}
          onScroll={handleScroll}
          onPrint={handlePalletsPrint}
          renderExpandedContent={renderExpandedContent}
        />
      </Box>
    </Box>
  )
}
